import { Ajax } from "../../shared/ajax";
import { ApiBQCol, ApiBQResponse, ApiAttribute, ApiUpdate, ApiConnection, ApiVizCreateRequest, ApiDisplay, ApiErrorResponse, ApiBQQuery } from "./api_data";
import { App, BQData, PivotMeasure } from "./app";
import { BQ } from "./bq";
import * as CodeMirror from 'codemirror';
import 'codemirror/mode/sql/sql.js';
import { Api, UpdateOpts } from "./api";
import { Display, DataConnection, TableConnection, FilterConnection } from "./display";
import { EditorPage } from "./editor_page";
import uuid = require("uuid");
import { Page, VizConnection } from "./page";
import { Overlay } from "../../shared/overlay";
import { Menu } from "../../shared/menu";

export enum ColumnFilter {
  NoFilter,
  NumericOnly,
}

export interface ColumnSelectionData {
  attributes: ApiAttribute[],
  connections: ApiConnection[],
  connection_id: string;
  col_data: BQData,
}

export interface SelectVal {
  label: string;
  value: string;
}

export class Editor {

  static date_groups: SelectVal[] = [
    {
      label: "Day",
      value: "Day",
    },
    {
      label: "Week",
      value: "ISOWeek",
    },
    {
      label: "Month",
      value: "Month",
    },
    {
      label: "Quarter",
      value: "Quarter",
    },
    {
      label: "Year",
      value: "Year",
    }
  ]
  static date_groups_plural: SelectVal[] = [
    {
      label: "Hours",
      value: "Hour",
    },
    {
      label: "Days",
      value: "Day",
    },
    {
      label: "Weeks",
      value: "ISOWeek",
    },
    {
      label: "Months",
      value: "Month",
    },
    {
      label: "Quarters",
      value: "Quarter",
    },
    {
      label: "Years",
      value: "Year",
    }
  ]
  static date_recent: SelectVal = {
    label: "In the past...",
    value: "Recent",
  }
  static date_current: SelectVal = {
    label: "In the current...",
    value: "Current",
  }
  static date_custom: SelectVal = {
    label: "Custom",
    value: "Custom",
  }
  static text_match: SelectVal = {
    label: "Is equal to",
    value: "Match",
  }
  static text_not_match: SelectVal = {
    label: "Is not equal to",
    value: "NotMatch",
  }
  static date_pivots = ["Day Of Week", "Month Of Year", "Quarter Of Year"];
  static date_pivot_values: { [id: string]: string[] } = {
    "Day Of Week": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    //"Day Of Month": [],
    "Month Of Year": ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
    "Quarter Of Year": ["Q1", "Q2", "Q3", "Q4"]
  };
  static operations: SelectVal[] = [
    {
      label: "Sum",
      value: "Sum",
    },
    {
      label: "Avg",
      value: "Avg",
    },
    {
      label: "Min",
      value: "Min",
    },
    {
      label: "Max",
      value: "Max",
    },
  ];
  static sort_orders: SelectVal[] = [
    {
      label: "Asc",
      value: "Asc",
    },
    {
      label: "Desc",
      value: "Desc",
    }
  ];
  static editor: CodeMirror.Editor | null = null;

  static column_key(dataset_id: string, table_id: string, column_name: string, pivot: string | null): string {
    const p = pivot ? "." + pivot : "";
    return dataset_id + "." + table_id + "." + column_name + p;
  }

  static data_key(data: BQData, include_pivot: boolean): string {
    const p = include_pivot ? data.pivot : null;
    return Editor.column_key(data.dataset_id, data.table_id, data.column_name, p);
  }

  static get_selected_column(page: Page): DataConnection | null {
    const viz_list: Display[] = [];
    for (const conn of page.viz_list) {
      viz_list.push(conn.viz);
    }
    for (const viz of viz_list) {
      for (const col of viz.selected_columns) {
        const id = viz.selected_table?.id + ":" + col.id;
        if (id === page.selected_element) {
          return col;
        }
      }
    }
    return null;
  }

  static get_selected_viz(page: Page): Display | null {
    const viz_list: Display[] = [];
    for (const conn of page.viz_list) {
      viz_list.push(conn.viz);
    }
    for (const viz of viz_list) {
      const count_id = viz.selected_table?.id + ":count";
      if (page.selected_element === count_id) {
        return viz;
      }
      if (viz.id === page.selected_element || viz.selected_table && viz.selected_table.id === page.selected_element) {
        return viz;
      }
      for (const col of viz.selected_columns) {
        const id = viz.selected_table?.id + ":" + col.id;
        if (id === page.selected_element) {
          return viz;
        }
      }
    }
    return null;
  }

  static set_selected_element(element: string | null) {
    if (!App.user) {
      return;
    }
    if (!App.selected_page) {
      return;
    }
    const attr: ApiAttribute = {
      parent: App.user.id,
      key: "selected_element",
      text: element,
      number: null,
    }
    const update: ApiUpdate = {
      attributes: [attr],
      connections: [],
    }
    App.selected_page.selected_element = element;
    Api.update_page(App.selected_page, update);
  }

  static set_data_panel_open(page: Page, open: boolean) {
    if (!App.user) {
      return;
    }
    const b = open ? 1 : 0;
    const attr: ApiAttribute = {
      parent: App.user.id,
      key: "data_panel_open",
      text: null,
      number: b,
    }
    const update: ApiUpdate = {
      attributes: [attr],
      connections: [],
    }
    page.data_panel_open = open;
    Api.update_page(page, update);
  }

  static column_selection_data(viz_id: string, table_connection: TableConnection, col: ApiBQCol, position: number): ColumnSelectionData {
    let group_by: string | null = null;
    if (BQ.date_col(col.type)) {
      group_by = "Day";
    }

    const sel: BQData = {
      project_id: table_connection.table.project_id,
      dataset_id: table_connection.table.dataset_id,
      table_id: table_connection.table.id,
      column_name: col.name,
      column_type: col.type,
      rows: [],
      pivoted: false,
      pivot_facet: null,
      pivot: null,
      pivot_op: null,
      pivot_target: null,
      pivot_measures: [],
      operation: null,
      loading: false,
      hidden: false,
      closed: false,
    }

    const conn: ApiConnection = {
      id: uuid(),
      parent: viz_id,
      key: "column",
      child: col.name,
      deleted: false,
    }
    const pos: ApiAttribute = {
      parent: conn.id,
      key: "position",
      text: null,
      number: position,
    }
    const attrs: ApiAttribute[] = [pos];
    const conns: ApiConnection[] = [conn];

    if (group_by) {
      const group_attr: ApiAttribute = {
        parent: conn.id,
        key: "group_by",
        text: group_by,
        number: null,
      }
      attrs.push(group_attr);
    }

    return {
      attributes: attrs,
      connections: conns,
      connection_id: conn.id,
      col_data: sel,
    }
  }

  static add_column(viz: Display, col: ApiBQCol) {
    if (viz.selected_table == null) {
      return;
    }

    const data = Editor.column_selection_data(viz.id, viz.selected_table, col, viz.selected_columns.length);

    const update: ApiUpdate = {
      attributes: data.attributes,
      connections: data.connections,
    }
    if (App.user) {
      const selection_attr: ApiAttribute = {
        parent: App.user.id,
        key: "selected_element",
        text: data.connection_id,
        number: null,
      }
      update.attributes.push(selection_attr);
    }
    Api.update_display(viz.id, update);

    const c: DataConnection = {
      viz_id: viz.id,
      id: data.connection_id,
      data: data.col_data,
      position: viz.selected_columns.length,
    }
    viz.selected_columns.push(c);
    // TODO: move to page
    //viz.selected_element = data.connection_id;
    App.render();
  }

  static remove_column(page: Page) {
    const selected_col = Editor.get_selected_column(page);
    if (App.input_focused || !selected_col || selected_col.data.pivot) {
      return;
    }
    const viz_list: Display[] = [];
    for (const conn of page.viz_list) {
      viz_list.push(conn.viz);
    }
    const filtered = [];
    const key = Editor.data_key(selected_col.data, false);
    const api_key = Editor.data_key(selected_col.data, true);
    const update: ApiUpdate = {
      attributes: [],
      connections: [],
    }
    for (const viz of viz_list) {
      let matching_viz = false;
      for (const s of viz.selected_columns) {
        const s_key = Editor.data_key(s.data, false);
        const s_api_key = Editor.data_key(s.data, true);
        if (s_key === key && s_api_key === api_key) {
          matching_viz = true;
          const conn: ApiConnection = {
            id: selected_col.id,
            parent: viz.id,
            key: "column",
            child: s.data.column_name,
            deleted: true,
          }
          update.connections.push(conn);
        } else {
          filtered.push(s);
        }
      }
      if (matching_viz) {
        viz.selected_columns = filtered;
        Api.update_display(viz.id, update);
        break;
      }
    }
    if (App.user) {
      const sel_attr: ApiAttribute = {
        parent: App.user?.id,
        key: "selected_element",
        text: null,
        number: null,
      }
      update.attributes.push(sel_attr);
    }
    App.render();

  }

  static remove_filter(display: Display, filter: FilterConnection) {
    const api_conn: ApiConnection = {
      id: filter.id,
      parent: display.id,
      key: "filter",
      child: filter.column,
      deleted: true,
    }
    const filtered: FilterConnection[] = [];
    for (const f of display.filters) {
      if (f.id !== filter.id) {
        filtered.push(f);
      }
    }
    display.filters = filtered;
    const update: ApiUpdate = {
      attributes: [],
      connections: [api_conn],
    }
    const opts: UpdateOpts = {
      update_thumbnail: false,
      data_changes: true,
    }
    Api.update_display(display.id, update, opts);
    App.render();
  }

  static filter_attributes(filter: FilterConnection): ApiAttribute[] {
    return [
      {
        parent: filter.id,
        key: "column",
        text: filter.column,
        number: null,
      },
      {
        parent: filter.id,
        key: "date_mode",
        text: filter.date_mode,
        number: null,
      },
      {
        parent: filter.id,
        key: "date_current_interval",
        text: filter.date_current_interval,
        number: null,
      },
      {
        parent: filter.id,
        key: "date_recent_interval",
        text: filter.date_recent_interval,
        number: null,
      },
      {
        parent: filter.id,
        key: "date_recent_count",
        text: null,
        number: filter.date_recent_count,
      },
      {
        parent: filter.id,
        key: "date_min",
        text: filter.date_min,
        number: null,
      },
      {
        parent: filter.id,
        key: "date_max",
        text: filter.date_max,
        number: null,
      },
      {
        parent: filter.id,
        key: "text_mode",
        text: filter.text_mode,
        number: null,
      },
      {
        parent: filter.id,
        key: "text_query",
        text: filter.text_query,
        number: null,
      }
    ]
  }

  static add_filter(display: Display, column: string) {
    const filter: FilterConnection = {
      id: uuid(),
      viz_id: display.id,
      column: column,
      date_mode: Editor.date_recent.value,
      date_current_interval: Editor.date_groups[2].value,
      date_recent_interval: Editor.date_groups[0].value,
      date_recent_count: 30,
      date_min: null,
      date_max: null,
      text_mode: Editor.text_match.value,
      text_query: null,
    }
    display.filters.push(filter);
    const api_conn: ApiConnection = {
      id: filter.id,
      parent: display.id,
      key: "filter",
      child: column,
      deleted: false,
    }
    const update: ApiUpdate = {
      attributes: Editor.filter_attributes(filter),
      connections: [api_conn],
    }
    const opts: UpdateOpts = {
      update_thumbnail: false,
      data_changes: true,
    }
    Api.update_display(display.id, update, opts);
    App.render();
  }

  static update_filter(display: Display, filter: FilterConnection) {
    const update: ApiUpdate = {
      attributes: Editor.filter_attributes(filter),
      connections: [],
    }
    const opts: UpdateOpts = {
      update_thumbnail: false,
      data_changes: true,
    }
    Api.update_display(display.id, update, opts);
    App.render();
  }

  static set_group_by(viz: Display, column: string | null, interval: string | null) {
    viz.group_by = column;
    viz.group_by_interval = interval;
    const a: ApiAttribute = {
      parent: viz.id,
      key: "group_by",
      text: column,
      number: null,
    };
    const int: ApiAttribute = {
      parent: viz.id,
      key: "group_by_interval",
      text: interval,
      number: null,
    };
    const update: ApiUpdate = {
      attributes: [a, int],
      connections: [],
    }
    const opts: UpdateOpts = {
      update_thumbnail: false,
      data_changes: true,
    }
    Api.update_display(viz.id, update, opts);
    App.render();
  }

  static set_sort(viz: Display, column: string | null, order: string | null) {
    viz.sort = column;
    viz.sort_order = order;
    const a: ApiAttribute = {
      parent: viz.id,
      key: "sort",
      text: column,
      number: null,
    };
    const int: ApiAttribute = {
      parent: viz.id,
      key: "sort_order",
      text: order,
      number: null,
    };
    const update: ApiUpdate = {
      attributes: [a, int],
      connections: [],
    }
    const opts: UpdateOpts = {
      update_thumbnail: false,
      data_changes: true,
    }
    Api.update_display(viz.id, update, opts);
    App.render();
  }

  static set_count(viz: Display, column: string | null) {
    // convert empty string to null
    const c = column === "" ? null : column;
    viz.count_target = c;
    const a: ApiAttribute = {
      parent: viz.id,
      key: "count",
      text: column,
      number: null,
    };
    const update: ApiUpdate = {
      attributes: [a],
      connections: [],
    }
    const opts: UpdateOpts = {
      update_thumbnail: false,
      data_changes: true,
    }
    Api.update_display(viz.id, update, opts);
    App.render();
  }

  static set_operation(viz: Display, conn: DataConnection, op: string | null) {
    conn.data.operation = op;
    const a: ApiAttribute = {
      parent: conn.id,
      key: "operation",
      text: op,
      number: null,
    };
    const update: ApiUpdate = {
      attributes: [a],
      connections: [],
    }
    const opts: UpdateOpts = {
      update_thumbnail: false,
      data_changes: true,
    }
    Api.update_display(viz.id, update, opts);
    App.render();
  }

  static add_pivot_values(display: Display, conn: DataConnection, values: string[], pivot_facet: string | null): ApiUpdate {
    const update: ApiUpdate = {
      connections: [],
      attributes: [],
    };

    if (!display.selected_table) {
      return update;
    }

    for (let i = 0; i < values.length; i++) {
      const val = values[i];
      const api_conn: ApiConnection = {
        id: uuid(),
        parent: conn.id,
        key: "pivot_value",
        child: val,
        deleted: false,
      }
      update.connections.push(api_conn);
      const pos: ApiAttribute = {
        parent: api_conn.id,
        key: "position",
        text: null,
        number: Number(conn.position + "." + String(i)),
      }
      update.attributes.push(pos);
      const p_sel: BQData = {
        project_id: display.selected_table.table.project_id,
        dataset_id: display.selected_table.table.dataset_id,
        table_id: display.selected_table.table.id,
        column_name: conn.data.column_name,
        column_type: conn.data.column_type,
        rows: [],
        pivoted: false,
        pivot_facet: pivot_facet,
        pivot: val,
        pivot_op: null,
        pivot_target: null,
        pivot_measures: [],
        operation: null,
        loading: false,
        hidden: false,
        closed: false,
      }
      let idx = 0;
      for (const c of display.selected_columns) {
        if (c.data.column_name === conn.data.column_name) {
          break;
        }
        idx++;
      }
      const p_conn: DataConnection = {
        viz_id: display.id,
        id: api_conn.id,
        data: p_sel,
        position: idx + 1 + i,
      }
      display.selected_columns.splice(idx + 1 + i, 0, p_conn);
    }
    return update;
  }

  static add_string_pivot(display: Display, conn: DataConnection) {
    const table_path = conn.data.dataset_id + "." + conn.data.table_id;
    const q: ApiBQQuery = {
      project_id: conn.data.project_id,
      dataset_id: conn.data.dataset_id,
      table_id: conn.data.table_id,
      query: "SELECT DISTINCT(" + conn.data.column_name + ") as " + conn.data.column_name + " FROM " + table_path + " ORDER BY " + conn.data.column_name + " ASC LIMIT 100;"
    }
    Ajax.post("/api/v1/bq_query", function (data: ApiBQResponse) {
      conn.data.loading = false;
      conn.data.pivoted = true;
      const values: string[] = [];
      for (let i = 0; i < data.rows.length; i++) {
        if (data.rows[i].length > 0) {
          values.push(data.rows[i][0]);
        }
      }
      const update = Editor.add_pivot_values(display, conn, values, null);
      const piv: ApiAttribute = {
        parent: conn.id,
        key: "pivoted",
        text: null,
        number: 1,
      }
      update.attributes.push(piv);
      const opts: UpdateOpts = {
        update_thumbnail: false,
        data_changes: true,
      }
      Api.update_display(conn.viz_id, update, opts);
      App.render();
    }, q);
  }

  static add_date_pivot(display: Display, conn: DataConnection) {
    const key = Editor.date_pivots[0];
    conn.data.pivoted = true;
    conn.data.pivot_facet = key;
    const values = Editor.date_pivot_values[key];
    const update = Editor.add_pivot_values(display, conn, values, key);
    const piv: ApiAttribute = {
      parent: conn.id,
      key: "pivoted",
      text: null,
      number: 1,
    }
    const facet: ApiAttribute = {
      parent: conn.id,
      key: "pivot_facet",
      text: key,
      number: null,
    }
    update.attributes.push(piv);
    update.attributes.push(facet);
    const opts: UpdateOpts = {
      update_thumbnail: false,
      data_changes: true,
    }
    Api.update_display(conn.viz_id, update, opts);
    App.render();
  }

  static add_pivot(display: Display, conn: DataConnection) {
    if (conn.data.column_type === "STRING") {
      Editor.add_string_pivot(display, conn);
    } else if (BQ.date_col(conn.data.column_type)) {
      Editor.add_date_pivot(display, conn);
    }
  }

  static remove_pivot(conn: DataConnection) {
    if (!App.selected_page) {
      return;
    }
    let viz: Display | null = null;
    for (const v of App.selected_page.viz_list) {
      if (v.id === conn.viz_id) {
        viz = v.viz;
        break;
      }
    }
    if (!viz) {
      return;
    }

    const attr: ApiAttribute = {
      parent: conn.id,
      key: "pivoted",
      text: null,
      number: null,
    }
    const update: ApiUpdate = {
      connections: [],
      attributes: [attr],
    };
    conn.data.pivoted = false;
    const filtered: DataConnection[] = [];
    const key = Editor.data_key(conn.data, false);
    for (const c of viz.selected_columns) {
      const k = Editor.data_key(c.data, false);
      if (k === key && c.data.pivot) {
        const api_conn: ApiConnection = {
          id: c.id,
          parent: conn.id,
          key: "pivot_value",
          child: c.data.pivot,
          deleted: true,
        }
        update.connections.push(api_conn);
      } else {
        filtered.push(c);
      }
    }
    viz.selected_columns = filtered;
    const opts: UpdateOpts = {
      update_thumbnail: false,
      data_changes: true,
    }
    Api.update_display(viz.id, update, opts);
    App.render();
  }

  static set_pivot_op(display: Display, conn: DataConnection, op: string | null) {
    conn.data.pivot_op = op;
    const key = Editor.data_key(conn.data, false);
    for (const c of display.selected_columns) {
      const k = Editor.data_key(c.data, false);
      if (k === key) {
        c.data.pivot_op = op;
      }
    }
    const op_attr: ApiAttribute = {
      parent: conn.id,
      key: "pivot_operation",
      text: op,
      number: null,
    }
    const update: ApiUpdate = {
      connections: [],
      attributes: [op_attr],
    };
    const opts: UpdateOpts = {
      update_thumbnail: false,
      data_changes: true,
    }
    Api.update_display(display.id, update, opts);
    App.render();
  }

  static add_pivot_measure(display: Display, conn: DataConnection) {
    if (!display.selected_table) {
      return;
    }
    let measure_col = "";
    if (display.selected_table.table.columns) {
      for (const c of display.selected_table.table.columns) {
        if (BQ.numeric_col(c.type)) {
          let existing = false;
          for (const m of conn.data.pivot_measures) {
            if (m.column === c.name) {
              existing = true;
              break;
            }
          }
          if (!existing) {
            measure_col = c.name;
            break;
          }
        }
      }
    }
    if (measure_col === "") {
      return;
    }
    const measure: PivotMeasure = {
      id: uuid(),
      column: measure_col,
    }
    conn.data.pivot_measures.push(measure);
    const api_conn: ApiConnection = {
      id: measure.id,
      parent: conn.id,
      key: "pivot_measure",
      child: measure.column,
      deleted: false,
    }
    const update: ApiUpdate = {
      connections: [api_conn],
      attributes: [],
    };
    const opts: UpdateOpts = {
      update_thumbnail: false,
      data_changes: true,
    }
    Api.update_display(display.id, update, opts);
    App.render();
  }

  static remove_pivot_measure(viz: Display, conn: DataConnection, measure: PivotMeasure) {
    const filtered: PivotMeasure[] = [];
    for (const m of conn.data.pivot_measures) {
      if (m !== measure) {
        filtered.push(m);
      }
    }
    conn.data.pivot_measures = filtered;
    const api_conn: ApiConnection = {
      id: measure.id,
      parent: conn.id,
      key: "pivot_measure",
      child: measure.column,
      deleted: true,
    }
    const update: ApiUpdate = {
      connections: [api_conn],
      attributes: [],
    };
    const opts: UpdateOpts = {
      update_thumbnail: false,
      data_changes: true,
    }
    Api.update_display(viz.id, update, opts);
    App.render();
  }

  static update_pivot_measure(viz: Display, conn: DataConnection, measure: PivotMeasure, new_column: string) {
    measure.column = new_column;
    const api_conn: ApiConnection = {
      id: measure.id,
      parent: conn.id,
      key: "pivot_measure",
      child: measure.column,
      deleted: false,
    }
    const update: ApiUpdate = {
      connections: [api_conn],
      attributes: [],
    };
    const opts: UpdateOpts = {
      update_thumbnail: false,
      data_changes: true,
    }
    Api.update_display(viz.id, update, opts);
    App.render();
  }

  static delete_selected_viz() {
    if (Editor.editor && Editor.editor.hasFocus()) {
      return;
    }
    if (Overlay.overlays.length > 0 || Menu.menu_open || App.input_focused || !App.selected_page) {
      return;
    }
    const selected_viz = Editor.get_selected_viz(App.selected_page);
    if (!selected_viz || selected_viz.id !== App.selected_page.selected_element) {
      return;
    }
    const page = App.selected_page;
    let conn: VizConnection | null = null;
    for (const v of page.viz_list) {
      if (v.viz.id === selected_viz.id) {
        conn = v;
        break;
      }
    }
    if (!conn) {
      return;
    }

    const conn_update: ApiConnection = {
      id: conn.id,
      parent: page.id,
      key: "viz",
      child: conn.viz.id,
      deleted: true,
    }
    const update: ApiUpdate = {
      attributes: [],
      connections: [conn_update],
    }
    const update_thumbnail = true;
    Api.update_page(page, update, update_thumbnail);
    const filtered = [];
    for (const v of page.viz_list) {
      if (v.id !== conn.id) {
        filtered.push(v);
      }
    }
    page.viz_list = filtered;
    App.render();
  }

  static set_visibility(viz: Display, conn: DataConnection, hidden: boolean) {
    conn.data.hidden = hidden;
    const h = hidden ? 1 : 0;
    const attr: ApiAttribute = {
      parent: conn.id,
      key: "hidden",
      text: null,
      number: h,
    }
    const update: ApiUpdate = {
      connections: [],
      attributes: [attr],
    };
    const opts: UpdateOpts = {
      update_thumbnail: false,
      data_changes: true,
    }
    Api.update_display(viz.id, update, opts);
    App.render();
  }

  static set_count_visibility(viz: Display, hidden: boolean) {
    viz.count.hidden = hidden;
    const h = hidden ? 1 : 0;
    const attr: ApiAttribute = {
      parent: viz.id,
      key: "count_hidden",
      text: null,
      number: h,
    }
    const update: ApiUpdate = {
      connections: [],
      attributes: [attr],
    };
    const opts: UpdateOpts = {
      update_thumbnail: false,
      data_changes: true,
    }
    Api.update_display(viz.id, update, opts);
    App.render();
  }

  static set_pivot_closed(viz: Display, conn: DataConnection, closed: boolean) {
    conn.data.closed = closed;
    const c = closed ? 1 : 0;
    const attr: ApiAttribute = {
      parent: conn.id,
      key: "closed",
      text: null,
      number: c,
    }
    const update: ApiUpdate = {
      connections: [],
      attributes: [attr],
    };
    const opts: UpdateOpts = {
      update_thumbnail: false,
      data_changes: false,
    }
    Api.update_display(viz.id, update, opts);
  }

  static set_pivot_sub_values_visibility(viz: Display, conn: DataConnection, hidden: boolean) {
    const update: ApiUpdate = {
      connections: [],
      attributes: [],
    };
    const key = Editor.data_key(conn.data, false);
    for (const s of viz.selected_columns) {
      const k = Editor.data_key(s.data, false);
      if (k === key && s.data.pivot) {
        s.data.hidden = hidden;
        const h = hidden ? 1 : 0;
        const attr: ApiAttribute = {
          parent: s.id,
          key: "hidden",
          text: null,
          number: h,
        }
        update.attributes.push(attr);
      }
    }
    const opts: UpdateOpts = {
      update_thumbnail: false,
      data_changes: true,
    }
    Api.update_display(viz.id, update, opts);
    App.render();
  }

  static set_viz_closed(viz: Display, closed: boolean) {
    viz.closed = closed;
    const c = closed ? 1 : 0;
    const attr: ApiAttribute = {
      parent: viz.id,
      key: "closed",
      text: null,
      number: c,
    }
    const update: ApiUpdate = {
      connections: [],
      attributes: [attr],
    };
    const opts: UpdateOpts = {
      update_thumbnail: false,
      data_changes: false,
    }
    Api.update_display(viz.id, update, opts);
  }

  static set_table_closed(viz: Display, closed: boolean) {
    if (!viz.selected_table) {
      return;
    }
    viz.selected_table.closed = closed;
    const c = closed ? 1 : 0;
    const attr: ApiAttribute = {
      parent: viz.selected_table.id,
      key: "closed",
      text: null,
      number: c,
    }
    const update: ApiUpdate = {
      connections: [],
      attributes: [attr],
    };
    const opts: UpdateOpts = {
      update_thumbnail: false,
      data_changes: false,
    }
    Api.update_display(viz.id, update, opts);
  }

  static add_new_viz() {
    if (!App.selected_page) {
      return;
    }
    const page = App.selected_page;

    // new viz
    const viz_id = uuid();
    const req: ApiVizCreateRequest = {
      id: viz_id,
      owner_id: App.selected_owner_id,
    }

    let list = page.viz_list;
    let group_key = "left";
    let left_height = 0;
    let right_height = 0;
    for (const v of page.viz_list) {
      const g_id = "viz_" + v.viz.id;
      const g = document.querySelector("svg #" + g_id) as SVGGElement;
      if (g) {
        if (v.group === "left") {
          left_height += g.getBoundingClientRect().height;
        } else {
          right_height += g.getBoundingClientRect().height;
        }
      }
    }
    if (left_height > right_height) {
      group_key = "right";
    }

    const conn: ApiConnection = {
      id: uuid(),
      parent: page.id,
      key: "viz",
      child: viz_id,
      deleted: false,
    }

    const group: ApiAttribute = {
      parent: conn.id,
      key: "group",
      text: group_key,
      number: null,
    }
    const pos: ApiAttribute = {
      parent: conn.id,
      key: "position",
      text: null,
      number: list.length,
    }
    const update: ApiUpdate = {
      attributes: [group, pos],
      connections: [conn],
    }

    Ajax.post("/api/v1/displays", function (data: ApiDisplay) {
      const viz = Display.parse(data, App.tables);
      const vc: VizConnection = {
        id: conn.id,
        viz: viz,
        position: list.length,
        group: group_key,
      }
      list.push(vc);
      const update_thumbnail = true;
      Api.update_page(page, update, update_thumbnail);
      Editor.set_selected_element(viz_id);
      App.render();
    }, req);
  }

  static zoom_in() {
    if (App.zoom >= 4) {
      return;
    }
    if (App.zoom < 0.5) {
      App.zoom = 0.5;
    } else if (App.zoom < 1) {
      App.zoom = 1;
    } else if (App.zoom < 2) {
      App.zoom = 2;
    } else if (App.zoom < 4) {
      App.zoom = 4;
    }

    document.body.style.setProperty("--zoom", App.zoom + "");
    EditorPage.update_zoom_ui();
  }

  static zoom_out() {
    if (App.zoom <= .25) {
      return;
    }
    if (App.zoom > 2) {
      App.zoom = 2;
    } else if (App.zoom > 1) {
      App.zoom = 1;
    } else if (App.zoom > 0.5) {
      App.zoom = 0.5;
    } else if (App.zoom > 0.25) {
      App.zoom = 0.25;
    }

    document.body.style.setProperty("--zoom", App.zoom + "");
    EditorPage.update_zoom_ui();
  }

  static zoom_to_fit() {
    const page = document.querySelector("div.graph-page");
    const center = document.querySelector("div.center");
    if (!page || !center) {
      return;
    }

    const padding = 15;
    const page_w = page.getBoundingClientRect().width / App.zoom;
    const page_h = page.getBoundingClientRect().height / App.zoom;
    const center_w = center.getBoundingClientRect().width - (padding * 2);
    const center_h = center.getBoundingClientRect().height - padding * 2;
    const w = center_w / page_w;
    const h = center_h / page_h;
    let z = Math.min(w, h);
    if (z > 1) {
      z = 1;
    }
    App.zoom = z;

    let x_off = 0;
    if (center_w < page_w) {
      x_off = padding + (center_w / 2) - (page_w / 2);
    }
    const y_off = padding + (center_h / 2) - (page_h / 2);

    App.translate_x = x_off;
    App.translate_y = y_off;
    document.body.style.setProperty("--translate-x", App.translate_x + "");
    document.body.style.setProperty("--translate-y", App.translate_y + "");
    document.body.style.setProperty("--zoom", App.zoom + "");
    EditorPage.update_zoom_ui();
    EditorPage.set_transform_origin();
  }

  static zoom_reset() {
    App.zoom = 1;
    document.body.style.setProperty("--zoom", App.zoom + "");
    EditorPage.update_zoom_ui();
  }

  static run(viz?: Display) {

    const run_viz = function (viz: Display) {
      App.updating_count++;
      const bottom = document.querySelector("div.bottom") as HTMLElement;
      const loading = document.querySelector("div.bottom div.loading-overlay") as HTMLElement;
      if (loading) {
        loading.style.display = "flex";
      }
      const button = document.querySelector("div.run-button") as HTMLElement;
      if (button) {
        button.classList.add("working");
      }
      App.thumbnail_dirty_count++;

      const on_complete = function () {
        App.updating_count--;
        if (!App.selected_page) {
          return;
        }
        EditorPage.render_data(App.selected_page, viz, bottom);
        const editor = document.querySelector("div.editor") as HTMLElement;
        EditorPage.render_main(editor);
        if (App.updating_count > 0) {
          return;
        }
        if (loading) {
          loading.style.display = "none";
        }
        // re-select element, in case page has been rendered again
        const button = document.querySelector("div.run-button") as HTMLElement;
        if (button) {
          button.classList.remove("working");
        }
      }
      if (!viz.selected_table) {
        return;
      }

      BQ.run(viz.selected_table.table, viz, function () {
        App.new_graph_data = true;
        on_complete();
      }, function (_data: ApiErrorResponse) {
        on_complete();
      });
    }

    if (viz !== undefined) {
      run_viz(viz);
    } else if (App.selected_page) {
      for (const v of App.selected_page.viz_list) {
        run_viz(v.viz);
      }
    }
  }
}
